import { useQuery } from '@apollo/client'
import { useTranslations } from 'next-intl'
import React, { ReactElement } from 'react'
import {
  Box,
  Icon,
  Grid,
  Text,
  useColorModeValue,
  Flex,
  Heading,
  Stack,
  Link,
  Button,
  TableContainer,
} from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'

import { getAllFacilities } from '@/api/facilities'
import { IFacilityNode } from '@/types/facilities.interface'
import { FacilityCard } from './FacilityCard'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { FacilitiesHero } from '@/features/facilities/FacilitiesHero'
import { LeafIcon } from '@/components/icons/LeafIcon'

import { MdFilterCenterFocus, MdOutlineSupport } from 'react-icons/md'
import { BsArrowRight, BsFillLightningChargeFill } from 'react-icons/bs'
import NextLink from 'next/link'
import { AllFacilitiesDataTable } from './AllFacilitiesDataTable'
import { FacilityListMobileCard } from './FacilitiesListMobileCard'

interface FeatureProps {
  title: string
  text: string
  icon: ReactElement
  iconBg: string
}

const facilitiesImagesToshow = [
  '/img/facilities_carousel_image_1.png',
  '/img/facilities_carousel_image_3.png',
  '/img/facilities_carousel_image_2.png',
]

const FacilityFeature = ({ title, text, icon, iconBg }: FeatureProps) => {
  return (
    <Stack maxWidth={{ base: 'full', sm: '360px' }} color={'white'} mb={8}>
      <Flex
        w={12}
        h={12}
        align={'center'}
        justify={'center'}
        color={'white'}
        bg={iconBg}
        rounded={'xl'}
        mb={1}
      >
        {icon}
      </Flex>
      <Box fontWeight={600} textAlign={'left'}>
        {title}
      </Box>
      <Box
        color={'white'}
        textAlign={'left'}
        fontSize={'1.2rem'}
        maxWidth={{ base: 'full', sm: '300px' }}
      >
        {text}
      </Box>
    </Stack>
  )
}

export function FacilitiesList() {
  const t = useTranslations()
  const gray50 = useColorModeValue('gray.50', 'black')
  const blackWhite = useColorModeValue('black', 'white')
  const btnBgColor = useColorModeValue('black', 'black.200')
  const btnTextColor = useColorModeValue('black', 'white')

  const scrollIntoViewForAllFacilities = () => {
    const element = document?.getElementById('AllFacilitiesScrollInto') || ''

    if (element) {
      element.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }

  const allCombinedFacilities = useQuery(getAllFacilities, {
    fetchPolicy: 'cache-first',
  })

  /* create column helper for all facilities table, get all attributes types */
  const allFacilitiesColumnsHelper = createColumnHelper<IFacilityNode>()

  /* build column headers for all facilities table */
  const allFacilitiesColumns = [
    allFacilitiesColumnsHelper.accessor('facilitiesName', {
      cell: (info) => info.getValue(),
      header: t('facilities-list.facility'),
    }),
    allFacilitiesColumnsHelper.accessor('facilityAvailability', {
      cell: (info) => info.getValue(),
      header: t('facilities-list.availability'),
    }),
    allFacilitiesColumnsHelper.accessor('facilitiesId', {
      cell: (info) => info.getValue(),
      header: '',
    }),
  ]

  return (
    <>
      <FacilitiesHero />
      <ContainerBase mb={16}>
        <Box w={'full'} py={7} rounded="xl">
          <Flex
            direction={{ base: 'column' }}
            w={'full'}
            mt={'3'}
            fontSize={'md'}
          >
            <Heading
              as={'h2'}
              fontSize={{ base: '3xl', xl: '38px' }}
              mb={4}
              w={'full'}
            >
              {t('facilities-list.featured-facilities')}
            </Heading>
            {t('facilities-list.featured-facilities-text')}
          </Flex>
        </Box>
        <Grid
          templateColumns={{
            sm: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          columnGap={9}
          rowGap={{ base: 5, md: 9, lg: 14 }}
        >
          {allCombinedFacilities?.data?.featuredFacilities?.nodes?.map(
            (facility: IFacilityNode) => (
              <FacilityCard key={facility?.facilitiesId} facility={facility} />
            )
          )}
        </Grid>
      </ContainerBase>

      <Flex justify={'center'} w={'full'}>
        <Text
          color={'brand.600'}
          fontWeight={'600'}
          _hover={{ textDecoration: 'none', opacity: 0.5, cursor: 'pointer' }}
          onClick={() => {
            scrollIntoViewForAllFacilities()
          }}
          mb={12}
        >
          {t('facilities-list.view-all-facilities')}{' '}
          <Icon as={BsArrowRight} h={4} w={4}></Icon>
        </Text>
      </Flex>

      {/* Mining Facilities Features Section */}
      <Stack
        direction={{ base: 'column', lg: 'row' }}
        bg={'black'}
        backgroundRepeat="no-repeat"
        minW={{ base: 'unset' }}
      >
        <ContainerBase justifyContent="center">
          <Flex
            width={'full'}
            justifyContent="center"
            flexDirection={{ base: 'column', lg: 'unset' }}
          >
            <Stack
              zIndex={1}
              direction="column"
              justifyContent="center"
              spacing={6}
              width={'100%'}
              mb={{ base: 8, xl: 0 }}
            >
              <Flex color="brand.600" justifyContent="center" />
              <Box
                fontSize={{ base: '1.3rem', lg: '1.3rem' }}
                textAlign="center"
                lineHeight="1.375"
                fontWeight="400"
                color={useColorModeValue('gray.500', 'gray.700')}
                px={{ base: '26px', lg: '2' }}
              >
                <Heading
                  as={'h2'}
                  fontSize={{ base: '5xl', md: '5xl' }}
                  w={'full'}
                  textAlign={'left'}
                  color={'white'}
                >
                  {t('facilities-list.worlds-best')}
                  <br />
                  {t('facilities-list.mining-facilities')}
                </Heading>
                <Box
                  textAlign={'left'}
                  fontSize={'xl'}
                  color={'white'}
                  lineHeight={'50px'}
                  mb={8}
                >
                  {t('facilities-list.available-at')}
                </Box>

                {/* First pair of features  */}
                <Flex flexDirection={{ base: 'column', sm: 'row' }}>
                  <FacilityFeature
                    iconBg={'#5AA2F7'}
                    icon={
                      <Icon
                        as={MdFilterCenterFocus}
                        w={8}
                        h={8}
                        rounded={'md'}
                      />
                    }
                    title={t('facilities-list.features.serving-title')}
                    text={t('facilities-list.features.serving-text')}
                  />
                  <FacilityFeature
                    iconBg={'#27AE60'}
                    icon={<LeafIcon w={7} h={7} />}
                    title={t('facilities-list.features.green-title')}
                    text={t('facilities-list.features.green-text')}
                  />
                </Flex>

                {/* Second pair of features  */}
                <Flex flexDirection={{ base: 'column', sm: 'row' }}>
                  <FacilityFeature
                    iconBg={'#F09742'}
                    icon={
                      <Icon as={MdOutlineSupport} w={8} h={8} rounded={'md'} />
                    }
                    title={t('facilities-list.features.support-title')}
                    text={t('facilities-list.features.support-text')}
                  />
                  <FacilityFeature
                    iconBg={'#F2C94C'}
                    icon={<Icon as={BsFillLightningChargeFill} w={6} h={6} />}
                    title={t('facilities-list.features.power-title')}
                    text={t('facilities-list.features.power-text')}
                  />
                </Flex>

                <Box textAlign={'left'} fontSize={'1.2rem'}>
                  <Box color={'white'}>
                    {t('facilities-list.interested-hosting')}
                  </Box>
                  <NextLink href={`/facility-application`} passHref>
                    <Link _hover={{ textDecoration: 'none', opacity: 0.9 }}>
                      {t('facilities-list.verified-hosting')}{' '}
                      <Icon as={BsArrowRight} h={4} w={4}></Icon>
                    </Link>
                  </NextLink>
                </Box>
              </Box>
            </Stack>

            <Flex
              flexDirection={'column'}
              alignSelf={{ base: 'unset', lg: 'center', xl: 'unset' }}
            >
              {facilitiesImagesToshow.map((imageURL, index, row) => {
                return (
                  <Box
                    borderBottomRadius={index === 0 ? '30px' : 'unset'}
                    borderTopRadius={index === 2 ? '30px' : 'unset'}
                    borderRadius={index === 1 ? '30px' : ''}
                    width={{ base: '100%', lg: 454 }}
                    height={{ base: 198, xl: index !== 1 ? 250 : 303 }}
                    bgImage={`url('${imageURL}')`}
                    bgPosition="center"
                    bgRepeat="no-repeat"
                    bgSize={'cover'}
                    flex={'none'}
                    marginBottom={index + 1 === row.length ? 0 : 6}
                    key={index}
                    mx={{ base: 'auto', xl: 'unset' }}
                  />
                )
              })}
            </Flex>
          </Flex>
          <Flex
            id="AllFacilitiesScrollInto"
            width={{ base: '', xl: '67%' }}
            display={{ base: 'none', xl: 'flex' }}
            bgImage={{
              base: 'none',
              md: 'url(/img/facilities-features-bg.png)',
            }}
            bgSize={'contain'}
          ></Flex>
        </ContainerBase>
      </Stack>

      {/* All Facilities Section */}
      <ContainerBase>
        <Box w={'full'} py={7} rounded="xl" mb={{ base: 0, md: 7 }}>
          <Flex direction={{ base: 'column' }} w={'full'} mt={'5'}>
            <Heading as={'h2'} fontSize={{ base: '4xl', md: '4xl' }} w={'full'}>
              {t('facilities-list.all-facilities')}
            </Heading>
          </Flex>
        </Box>

        {/* Facilities Table */}
        <Box my={10} textTransform={'none'}>
          <TableContainer display={{ base: 'none', md: 'block' }}>
            <AllFacilitiesDataTable
              columns={allFacilitiesColumns}
              data={allCombinedFacilities?.data?.allFacilities?.nodes || []}
            />
          </TableContainer>

          {/* mobile */}
          <Box display={{ base: 'block', md: 'none' }}>
            <Text
              size={'sm'}
              mb={4}
              background={useColorModeValue('gray.40', 'black')}
              py={2}
              pl={4}
              mt={-10}
              color={useColorModeValue('black', 'white')}
            >
              Order by Availability: Available First
            </Text>
            {allCombinedFacilities?.data?.allFacilities?.nodes?.map(
              (facility: IFacilityNode) =>
                facility?.facilityAvailability === 'available' && (
                  <React.Fragment key={facility?.facilitiesId}>
                    <FacilityListMobileCard
                      facilitiesId={facility?.facilitiesId}
                      facilitiesName={facility?.facilitiesName}
                      availabilityText={t('facilities-list.units-available')}
                      availabilityTextColor={'green.450'}
                    />
                  </React.Fragment>
                )
            )}
            {allCombinedFacilities?.data?.allFacilities?.nodes?.map(
              (facility: IFacilityNode) =>
                facility?.facilityAvailability === 'coming_soon' && (
                  <React.Fragment key={facility?.facilitiesId}>
                    <FacilityListMobileCard
                      facilitiesId={facility?.facilitiesId}
                      facilitiesName={facility?.facilitiesName}
                      availabilityText={t('facilities-list.coming-soon')}
                      availabilityTextColor={'orange.450'}
                    />
                  </React.Fragment>
                )
            )}
            {allCombinedFacilities?.data?.allFacilities?.nodes?.map(
              (facility: IFacilityNode) =>
                facility?.facilityAvailability === 'fully_booked' && (
                  <React.Fragment key={facility?.facilitiesId}>
                    <FacilityListMobileCard
                      facilitiesId={facility?.facilitiesId}
                      facilitiesName={facility?.facilitiesName}
                      availabilityText={t('facilities-list.fully-booked')}
                      availabilityTextColor={'red.550'}
                    />
                  </React.Fragment>
                )
            )}
          </Box>
        </Box>
      </ContainerBase>
      {/* Bottom Section before Footer */}
      <Stack
        mb={{ base: '-3rem', md: '-4rem' }}
        py={{ base: 0, md: 7, lg: '2.8rem' }}
        direction={{ base: 'column', lg: 'row' }}
        bg={gray50}
        height={{ base: 750, md: 700, lg: 400 }}
        zIndex={'5'}
        bgImage={{
          base: 'url(/img/facility-join-banner-mobile.png)',
          lg: 'url(/img/facility-join-banner-w1280px.png)',
          '2xl': 'url(/img/facility-join-banner.png)',
        }}
        bgPosition="center"
        bgSize="cover"
        bgRepeat="no-repeat"
      >
        <Flex width={'full'} justifyContent="center">
          <Stack
            zIndex={1}
            spacing={6}
            px={{ base: '2rem', md: 0 }}
            mt={{ base: 0, xl: '1rem' }}
            marginLeft={{ lg: '-23rem', xl: '-31rem', '2xl': '-38rem' }}
            alignSelf={{ base: 'center', xl: 'unset' }}
          >
            <Flex color="brand.600" justifyContent="center"></Flex>
            <Box
              textAlign="center"
              lineHeight="1.375"
              color={useColorModeValue('gray.500', 'gray.200')}
            >
              <Box
                fontSize="2xl"
                fontWeight={'bold'}
                textAlign={'left'}
                fontFamily={'Inter'}
                color={blackWhite}
              >
                {t('facilities-list.host-with-compass')}
              </Box>
              <Box
                textAlign={'left'}
                fontSize={'16px'}
                fontFamily={'Inter'}
                color={blackWhite}
                maxWidth={{ base: '', md: '492px' }}
                my={3}
              >
                {t('facilities-list.host-with-compass-description')}
              </Box>
              <Box textAlign="left">
                <NextLink href={`/facility-application`} passHref>
                  <Button
                    fontSize={'1rem'}
                    fontWeight={'normal'}
                    fontFamily={'Inter'}
                    bg={btnBgColor}
                    color={'white'}
                    py={{ base: 7, md: 2 }}
                    textAlign="left"
                    maxWidth={{ base: '255px', md: '100%' }}
                    whiteSpace="break-spaces"
                  >
                    {t('facilities-list.verified-hosting')}{' '}
                  </Button>
                </NextLink>
              </Box>
            </Box>
          </Stack>
        </Flex>
      </Stack>
    </>
  )
}
